/**
 * Project write helpers — web SDK equivalents of
 * `src/features/projects/projects.ts`.
 *
 * - `updateProject`: direct Firestore `updateDoc` on
 *   `projects/{projectId}`. Rules gate write access (org admins +
 *   project members per the org-role map).
 * - `deleteProject`: wraps the existing `deleteProjectCascade`
 *   Cloud Function so the server cleans up R2 objects + every
 *   project-scoped Firestore doc (tasks, transactions, dpr, etc).
 *   Don't write a client-side delete — it'd orphan storage.
 */
import {
  deleteField,
  doc,
  serverTimestamp,
  Timestamp,
  updateDoc,
} from 'firebase/firestore';

import { db, functions, httpsCallable } from '@/lib/firebase';

// ProjectStatus, ProjectTypology, ProjectPhase + helpers now live
// in `@shared/perms/project` so mobile and web read identical
// values. Re-exported here so existing
// `import { ProjectPhase } from '@/lib/projects'` call sites keep
// working unchanged.
import type {
  ProjectStatus,
  ProjectTypology,
  ProjectPhase,
} from '@shared/perms';
export type { ProjectStatus, ProjectTypology, ProjectPhase };
export { PROJECT_PHASES, phaseLabel } from '@shared/perms';

export type UpdateProjectInput = {
  projectId: string;
  name?: string;
  startDate?: Date | null;
  endDate?: Date | null;
  siteAddress?: string;
  value?: number;
  status?: ProjectStatus;
  client?: string;        // empty string = clear
  location?: string;      // empty string = clear
  typology?: ProjectTypology;
  subType?: string;       // empty string = clear
  progress?: number;
  /** Workflow phase. Pass `null` to clear. */
  phase?: ProjectPhase | null;
};

export async function updateProject(input: UpdateProjectInput): Promise<void> {
  const patch: Record<string, unknown> = {
    updatedAt: serverTimestamp(),
  };

  if (input.name !== undefined) patch.name = input.name.trim();
  if (input.siteAddress !== undefined) patch.siteAddress = input.siteAddress.trim();
  if (input.location !== undefined) {
    const v = input.location.trim();
    patch.location = v.length ? v : deleteField();
  }
  if (input.client !== undefined) {
    const v = input.client.trim();
    patch.client = v.length ? v : deleteField();
  }
  if (input.typology !== undefined) patch.typology = input.typology;
  if (input.subType !== undefined) {
    const v = input.subType.trim();
    patch.subType = v.length ? v : deleteField();
  }
  if (input.status) patch.status = input.status;
  if (input.phase !== undefined) {
    patch.phase = input.phase === null ? deleteField() : input.phase;
  }

  if (input.startDate !== undefined) {
    patch.startDate = input.startDate ? Timestamp.fromDate(input.startDate) : null;
  }
  if (input.endDate !== undefined) {
    patch.endDate = input.endDate ? Timestamp.fromDate(input.endDate) : null;
  }

  if (input.value !== undefined && !Number.isNaN(input.value)) {
    patch.value = Math.max(0, Math.round(input.value));
  }
  if (input.progress !== undefined && !Number.isNaN(input.progress)) {
    patch.progress = Math.max(0, Math.min(100, Math.round(input.progress)));
  }

  // Only `updatedAt` set? Skip the write — nothing to update.
  if (Object.keys(patch).length === 1) return;

  await updateDoc(doc(db, 'projects', input.projectId), patch);
}

/**
 * Cascade-delete a project + all its child docs + R2 objects via
 * the deployed `deleteProjectCascade` callable.
 */
const deleteProjectCascadeCallable = httpsCallable<
  { projectId: string },
  { ok: true; deletedR2: number; deletedDocs: number }
>(functions, 'deleteProjectCascade');

export async function deleteProject(projectId: string): Promise<{
  deletedR2: number;
  deletedDocs: number;
}> {
  const res = await deleteProjectCascadeCallable({ projectId });
  return {
    deletedR2: res.data.deletedR2 ?? 0,
    deletedDocs: res.data.deletedDocs ?? 0,
  };
}
